import { NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';

import {
    EventModel,
    FootballScore,
    LeaderboardGameScore,
    ScoreboardType,
    getScoreboardType,
    isBespokeScoreboard,
} from '@frontend/sports/betting-offer/feature/model';
import { MediaConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { DeviceService } from '@frontend/vanilla/core';

import { HeaderContentDensity } from '../event-details-common.models';
import { BaseballScoreboardComponent } from './baseball-scoreboard.component';
import { CricketScoreboardComponent } from './cricket-scoreboard.component';
import { DartsScoreboardComponent } from './darts-scoreboard.component';
import { LeaderboardGameScoreboardComponent } from './leaderboard-game-scoreboard.component';
import { PairGameScoreboardComponent } from './pair-game-scoreboard.component';
import { PeriodGameScoreboardComponent } from './period-game-scoreboard.component';
import { PrematchScoreboardComponent } from './prematch-scoreboard.component';
import { PrematchTournamentScoreboardComponent } from './prematch-tournament-scoreboard.component';
import { SetGameScoreboardComponent } from './set-game-scoreboard.component';
import { SnookerScoreboardComponent } from './snooker-scoreboard.component';
import { SpecialGameScoreboardComponent } from './special-game-scoreboard.component';
import { StatisticsButtonComponent } from './statistics-button.component';
import { TournamentScoreboardComponent } from './tournament-scoreboard.component';

@Component({
    selector: 'ms-scoreboard',
    templateUrl: 'scoreboard.component.html',
    imports: [
        NgIf,
        NgSwitchCase,
        NgSwitch,
        BaseballScoreboardComponent,
        DartsScoreboardComponent,
        LeaderboardGameScoreboardComponent,
        PairGameScoreboardComponent,
        CricketScoreboardComponent,
        PeriodGameScoreboardComponent,
        SetGameScoreboardComponent,
        SnookerScoreboardComponent,
        SpecialGameScoreboardComponent,
        StatisticsButtonComponent,
        TournamentScoreboardComponent,
        PrematchScoreboardComponent,
        PrematchTournamentScoreboardComponent,
    ],
})
export class ScoreboardComponent implements OnChanges, OnInit {
    @Input() event: EventModel;
    @Input() size = HeaderContentDensity.Small;
    @Input() hideTimer = false;
    @Input() isShowcase = false;

    type: ScoreboardType;
    scoreboardType = ScoreboardType;
    isBespokeScoreboard: boolean;
    @HostBinding('class.edp-scoreboard') @Input() isNewEDPScoreboard = false;

    showStatsIcon: boolean;
    private backgroundSize = {
        [HeaderContentDensity.Fullscreen]: 'fullscreen',
        [HeaderContentDensity.Large]: 'large',
        [HeaderContentDensity.Medium]: 'medium',
        [HeaderContentDensity.Small]: 'small',
        [HeaderContentDensity.Tiny]: 'tiny',
    };

    @HostBinding()
    get class(): string {
        const firstClass = this.getHostFirstClass();

        return [firstClass, ...this.scoreboardStyle].join(' ');
    }

    get penaltyShootout(): boolean {
        return !!this.event.scoreboard.getScore<FootballScore>()!.penaltyShootOutScore;
    }

    private scoreboardStyle: string[] = [];

    constructor(
        private deviceService: DeviceService,
        private mediaConfig: MediaConfig,
    ) {}

    ngOnInit(): void {
        this.setScoreboardStyle();
    }

    ngOnChanges(): void {
        this.type = getScoreboardType(this.event);
        this.isBespokeScoreboard = isBespokeScoreboard(this.event);
        this.setScoreboardStyle();

        this.showStatsIcon = this.mediaConfig.showStatsCenterButton && this.size !== HeaderContentDensity.Tiny;
    }

    showPreMatchTimer(): boolean {
        const countdownTimerThreshold = 60 * 60 * 1000;

        return !this.event.scoreboard.started && this.event.startDate.getTime() - Date.now() <= countdownTimerThreshold && !this.hideTimer;
    }

    private getDensity(size: HeaderContentDensity): string {
        if (size === HeaderContentDensity.Auto) {
            return '';
        }
        return this.backgroundSize[size];
    }

    private setScoreboardStyle(): void {
        const scoreboardStyle = [];

        if (this.event.scoreboard.started) {
            scoreboardStyle.push('started');
        }
        if (this.showPreMatchTimer()) {
            scoreboardStyle.push('starting-in');
        }

        this.size =
            this.isNewEDPScoreboard && this.size === HeaderContentDensity.Tiny
                ? this.event.scoreboard.started
                    ? HeaderContentDensity.Medium
                    : HeaderContentDensity.Small
                : this.size;
        scoreboardStyle.push(this.getDensity(this.size));
        if (
            this.event.scoreboard.started &&
            (this.event.sport.id === SportConstant.Formula1 ||
                this.event.sport.id === SportConstant.Skialpine ||
                this.event.sport.id === SportConstant.Biathlon ||
                this.event.sport.id === SportConstant.Motorbikes)
        ) {
            const score = this.event.scoreboard.score as LeaderboardGameScore;
            if (score.participantsDetails.length >= 1) {
                scoreboardStyle.push('scored');
                scoreboardStyle.push('bespoke-scoreboard-container');
            }
        }

        if (this.isBespokeScoreboard) {
            scoreboardStyle.push('bespoke-scoreboard-container');
        } else {
            scoreboardStyle.push('fixed');
        }

        if (this.shouldShowEventDescription()) {
            scoreboardStyle.push('with-event-description');
        }

        if (this.type === ScoreboardType.SetGame) {
            scoreboardStyle.push('set-game');
        }

        this.scoreboardStyle = scoreboardStyle;
    }

    private shouldShowEventDescription(): boolean {
        if (!this.event.description) {
            return false;
        }

        if (this.type === ScoreboardType.LeaderboardGame || this.type === ScoreboardType.TournamentGame) {
            return false;
        }

        if (this.isShowcase) {
            return false;
        }

        return true;
    }

    private getHostFirstClass(): string {
        if (
            this.deviceService.isMobile &&
            this.event.participants.length &&
            ((this.event.participants[0].image && this.event.participants[0].image.logo) ||
                (this.event.participants.length === 2 && this.event.participants[1].image && this.event.participants[1].image.logo))
        ) {
            return 'scoreboard fading';
        }

        return 'scoreboard';
    }
}
